import React from 'react';
import { motion } from 'framer-motion';
import { Zap } from 'lucide-react';
import useComparisonStore from '../store/comparisonStore';
import { contentVariants } from '../utils/animations';

/**
 * SpeedSelector component
 * Allows users to select a speed range for filtering packages
 * Updated to use Zustand store instead of React Context
 */
const SpeedSelector = () => {
  const {
    speedRange,
    setSpeedRange,
    recommendedSpeed,
    openComparisonModal,
    isComparisonModalOpen
  } = useComparisonStore();
  
  const handleSpeedChange = (e) => {
    setSpeedRange([speedRange[0], parseInt(e.target.value)]);
    
    // Automatically open the comparison modal if it's not already open
    if (!isComparisonModalOpen) {
      openComparisonModal();
    }
  };
  
  const handleMinSpeedChange = (e) => {
    setSpeedRange([parseInt(e.target.value), speedRange[1]]);
    
    // Automatically open the comparison modal if it's not already open
    if (!isComparisonModalOpen) {
      openComparisonModal();
    }
  };
  
  // Predefined speed tiers for quick selection
  const speedTiers = [
    { label: '10 Mbps', value: 10 },
    { label: '20 Mbps', value: 20 },
    { label: '50 Mbps', value: 50 },
    { label: '100 Mbps', value: 100 },
    { label: '200 Mbps', value: 200 },
    { label: '500 Mbps', value: 500 },
    { label: '1 Gbps', value: 1000 }
  ];
  
  const selectSpeedTier = (value) => {
    // Set a range around the selected speed tier (±20%)
    const min = Math.max(0, Math.floor(value * 0.8));
    const max = Math.ceil(value * 1.2);
    setSpeedRange([min, max]);
    
    // Automatically open the comparison modal if it's not already open
    if (!isComparisonModalOpen) {
      openComparisonModal();
    }
  };
  
  return (
    <motion.div 
      className="space-y-4"
      variants={contentVariants}
    >
      <h3 className="text-sm font-medium text-white">Speed Range</h3>
      
      {/* Quick speed selection buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {speedTiers.map((tier, index) => (
          <motion.button
            key={index}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
              tier.value === recommendedSpeed
                ? 'bg-green-600 text-white' // Highlight recommended speed
                : speedRange[0] <= tier.value && speedRange[1] >= tier.value
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            onClick={() => selectSpeedTier(tier.value)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {tier.value === recommendedSpeed && (
              <Zap className="h-3 w-3 mr-1 inline" />
            )}
            {tier.label}
          </motion.button>
        ))}
      </div>
      
      {/* Custom speed range sliders */}
      <div className="space-y-4">
        {/* Min speed slider */}
        <div className="space-y-2">
          <div className="flex justify-between mb-1">
            <label className="text-xs font-medium text-gray-300">Minimum Speed</label>
            <span className="text-xs text-gray-400">
              {speedRange[0]} Mbps
            </span>
          </div>
          <input
            type="range"
            min="0"
            max={speedRange[1]}
            step="10"
            value={speedRange[0]}
            onChange={handleMinSpeedChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
        </div>
        
        {/* Max speed slider */}
        <div className="space-y-2">
          <div className="flex justify-between mb-1">
            <label className="text-xs font-medium text-gray-300">Maximum Speed</label>
            <span className="text-xs text-gray-400">
              {speedRange[1] === 1000 ? '1 Gbps' : `${speedRange[1]} Mbps`}
            </span>
          </div>
          <input
            type="range"
            min={speedRange[0]}
            max="1000"
            step="10"
            value={speedRange[1]}
            onChange={handleSpeedChange}
            className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-500"
          />
        </div>
      </div>
      
      {/* Current speed range display */}
      <div className="bg-gray-800 rounded-lg p-3 flex justify-between items-center">
        <span className="text-sm text-white font-medium">Current Range:</span>
        <span className="text-sm text-purple-400">
          {speedRange[0]} Mbps - {speedRange[1] === 1000 ? '1 Gbps' : `${speedRange[1]} Mbps`}
        </span>
      </div>
      
      {/* Recommended speed indicator */}
      {recommendedSpeed && (
        <motion.div 
          className="bg-green-900/30 border border-green-700/30 rounded-lg p-3"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex items-center text-green-400 font-medium mb-1">
            <Zap className="h-4 w-4 mr-1.5" />
            <span>Recommended Speed: {recommendedSpeed} Mbps</span>
          </div>
          <p className="text-xs text-gray-300">
            Based on your usage profile, we recommend packages around this speed.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
};

export default SpeedSelector;